import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import Modal from '../components/common/Modal';
import RecordAuditBlock from '../components/common/RecordAuditBlock';
import { createPaymentAccount, updatePaymentAccount, deletePaymentAccount, createManualTransaction, createExpense, deleteExpense } from '../services/firestoreService';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Pagination from '../components/common/Pagination';
import usePagination from '../hooks/usePagination';
import { PAYMENT_TYPES, EXPENSE_CATEGORIES, CATEGORY_TO_DEPARTMENT } from '../utils/constants';
import { formatCurrency, formatDate, parseDateInputValue } from '../utils/helpers';
import toast from 'react-hot-toast';

const PaymentAccounts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;
  const { paymentAccounts, expenses, transactions, employees, stats, loadingPayments, loadingExpenses } = useApp();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedExpenseDetail, setSelectedExpenseDetail] = useState(null);
  
  const [activeTab, setActiveTab] = useState('revenue'); // 'revenue' or 'expenses'
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [accountForm, setAccountForm] = useState({
    paymentType: '',
    accountName: '',
    accountNumber: ''
  });

  const [adjustmentAmount, setAdjustmentAmount] = useState('');

  const PAYMENT_LOGOS = {
    'EVC Plus': '/evc.png',
    'Dahabshiil Bank': '/dahabshiil.png',
    'Salaam Bank': '/salam.png',
    'Primmer Bank': '/primer.png',
    'IBS Bank': '/ibs.png',
  };

  const [expenseForm, setExpenseForm] = useState({
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    employeeId: ''
  });

  // ============ Account Functions ============
  const handleAccountChange = (e) => {
    setAccountForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openAddAccountModal = () => {
    setEditingItem(null);
    setAccountForm({ paymentType: '', accountName: '', accountNumber: '' });
    setAdjustmentAmount('');
    setShowAccountModal(true);
  };

  const openEditAccountModal = (account) => {
    setEditingItem(account);
    setAccountForm({
      paymentType: account.paymentType,
      accountName: account.accountName,
      accountNumber: account.accountNumber
    });
    setAdjustmentAmount('');
    setShowAccountModal(true);
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!accountForm.paymentType || !accountForm.accountName || !accountForm.accountNumber) {
      toast.error('Buuxi dhammaan meelaha');
      return;
    }
    setSubmitting(true);
    let result;
    const finalData = {
      ...accountForm
    };

    if (editingItem) {
      result = await updatePaymentAccount(editingItem.id, finalData);
    } else {
      result = await createPaymentAccount(finalData);
    }
    
    if (result.success) {
      toast.success(editingItem ? 'Waa la cusbooneysiiyay' : 'Waa lagu daray');
      setShowAccountModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleAdjustBalance = async (type) => {
    const amount = parseFloat(adjustmentAmount);
    if (!amount || amount <= 0) {
      toast.error('Geli lacag sax ah');
      return;
    }

    setSubmitting(true);
    
    // Calculate the TRUE current total balance from transactions
    const accountTrans = transactions.filter(t => t.paymentAccountId === editingItem?.id);
    const currentTotalBalance = accountTrans.reduce((sum, t) => sum + (t.amount || 0), 0);

    if (type === 'subtract' && amount > currentTotalBalance) {
      toast.error('lacagta aad kajarayso ma dhamo lacagta ad hada heystao');
      setSubmitting(false);
      return;
    }

    const result = await createManualTransaction(
      editingItem.id, 
      amount, 
      type, 
      editingItem.accountName, 
      editingItem.paymentType,
      auditActor
    );

    if (result.success) {
      toast.success(type === 'add' ? `+${amount} waa lagu daray` : `-${amount} waa laga jaray`);
      setShowAccountModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleDeleteAccount = async (account) => {
    if (window.confirm(`Ma hubtaa inaad tirtirto akoonka ${account.accountName}?`)) {
      const result = await deletePaymentAccount(account.id);
      if (result.success) {
        toast.success('Akoonka waa la tirtiray');
      } else {
        toast.error('Waa la diiday: ' + result.error);
      }
    }
  };

  // ============ Expense Functions ============
  const handleExpenseChange = (e) => {
    setExpenseForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openAddExpenseModal = () => {
    setEditingItem(null);
    setExpenseForm({
      category: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      employeeId: ''
    });
    setShowExpenseModal(true);
  };

  // Get matching department for selected expense category
  const matchedDepartment = CATEGORY_TO_DEPARTMENT[expenseForm.category] || null;
  const filteredEmployees = matchedDepartment
    ? employees.filter(emp => emp.department === matchedDepartment)
    : [];

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.category || !expenseForm.amount) {
      toast.error('Buuxi category iyo amount');
      return;
    }
    // If category maps to a department, employee is required
    if (matchedDepartment && !expenseForm.employeeId) {
      toast.error('Fadlan dooro shaqaalaha');
      return;
    }
    setSubmitting(true);

    // Find selected employee name
    const selectedEmployee = employees.find(emp => emp.id === expenseForm.employeeId);

    const result = await createExpense({
      category: expenseForm.category,
      description: expenseForm.description,
      amount: parseFloat(expenseForm.amount) || 0,
      date: parseDateInputValue(expenseForm.date),
      employeeId: expenseForm.employeeId || null,
      employeeName: selectedEmployee ? selectedEmployee.name : null,
      auditActor
    });
    if (result.success) {
      toast.success('Expense waa lagu daray');
      setShowExpenseModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleDeleteExpense = async (expense) => {
    if (window.confirm('Ma hubtaa inaad tirtirto?')) {
      const result = await deleteExpense(expense.id);
      if (result.success) {
        toast.success('Waa la tirtiray');
      } else {
        toast.error('Khalad');
      }
    }
  };

  // Calculate account stats
  const accountStats = paymentAccounts.map(account => {
    const accountTrans = transactions.filter(t => t.paymentAccountId === account.id);
    return {
      ...account,
      totalReceived: accountTrans.reduce((sum, t) => sum + (t.amount || 0), 0),
      transactionCount: accountTrans.length
    };
  });

  const accountPaging = usePagination(accountStats, { persistKey: 'accounts' });
  const expensePaging = usePagination(expenses, { persistKey: 'expenses' });

  const recentTransactions = useMemo(() => {
    return [...transactions]
      .sort((a, b) => {
        const ta = a.transactionDate?.seconds || a.createdAt?.seconds || 0;
        const tb = b.transactionDate?.seconds || b.createdAt?.seconds || 0;
        return tb - ta;
      })
      .slice(0, 40);
  }, [transactions]);

  return (
    <div className="space-y-4">
      <PageHero
        accent="green"
        eyebrow="Lacagaha"
        title="Dakhli & Kharash"
        subtitle="Maarayn dakhli iyo kharash"
        stats={[
          { label: 'Dakhli Guud', value: formatCurrency(stats.totalRevenue), hint: 'Invoice-yada la ansixiyay' },
          { label: 'Kharash Guud', value: formatCurrency(stats.totalExpenses) },
          { label: "Faa'iido Saafi", value: formatCurrency(stats.netProfit), hint: `${formatCurrency(stats.totalRevenue)} − ${formatCurrency(stats.totalExpenses)}` }
        ]}
      />

      <div className="flex gap-2 bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
        <button
          onClick={() => setActiveTab('revenue')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition ${
            activeTab === 'revenue' 
              ? 'bg-emerald-50 text-emerald-700 shadow-sm' 
              : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          💰 Dakhli (Revenue)
        </button>
        <button
          onClick={() => setActiveTab('expenses')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition ${
            activeTab === 'expenses' 
              ? 'bg-rose-50 text-rose-700 shadow-sm' 
              : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          💸 Kharash (Expenses)
        </button>
      </div>

      {/* Revenue Tab */}
      {activeTab === 'revenue' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openAddAccountModal} size="sm" icon={<span>➕</span>}>
              Ku dar Akoon
            </Button>
          </div>

          {loadingPayments ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-green-600 rounded-full animate-spin mx-auto"></div>
            </div>
          ) : accountStats.length > 0 ? (
            <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accountPaging.pagedItems.map((account) => (
                <div 
                  key={account.id}
                  className="bg-white rounded-3xl shadow-sm border border-slate-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center p-1.5 shadow border border-gray-100 flex-shrink-0">
                        <img 
                          src={PAYMENT_LOGOS[account.paymentType] || '/logo.png'} 
                          alt={account.paymentType}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="ml-3">
                        <h3 className="font-bold text-gray-900">{account.paymentType}</h3>
                        <p className="text-sm text-gray-500">{account.accountName}</p>
                        <p className="text-sm font-mono text-blue-600">{account.accountNumber}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => openEditAccountModal(account)}
                        className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                        title="Wax ka bedel"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteAccount(account)}
                        className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                        title="Tirtir"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t flex justify-between">
                    <div>
                      <p className="text-xs text-gray-500">La Helay</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(account.totalReceived)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Transactions</p>
                      <p className="text-lg font-bold text-gray-900">{account.transactionCount}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border">
              <Pagination
                page={accountPaging.page}
                totalPages={accountPaging.totalPages}
                total={accountPaging.total}
                pageSize={accountPaging.pageSize}
                onPageChange={accountPaging.setPage}
                onPageSizeChange={accountPaging.setPageSize}
              />
            </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 text-center">
              <p className="text-gray-500">Akoon lama helin</p>
              <Button onClick={openAddAccountModal} variant="outline" className="mt-4">
                Ku dar Akoon
              </Button>
            </div>
          )}

          {recentTransactions.length > 0 && (
            <Card title="Dhammaan transactions (riix faahfaahin)">
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {recentTransactions.map((tx) => (
                  <button
                    key={tx.id}
                    type="button"
                    onClick={() => setSelectedTransaction(tx)}
                    className="w-full text-left p-3 rounded-lg border hover:bg-slate-50 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium text-sm">{tx.invoiceNumber || tx.customerName}</p>
                      <p className="text-xs text-gray-500">{tx.paymentType} · {formatDate(tx.transactionDate || tx.createdAt, 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                    <span className={`font-bold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(tx.amount)}
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openAddExpenseModal} size="sm" variant="danger" icon={<span>➕</span>}>
              Ku dar Kharash
            </Button>
          </div>

          {loadingExpenses ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin mx-auto"></div>
            </div>
          ) : expenses.length > 0 ? (
            <div className="space-y-3">
              {expensePaging.pagedItems.map((expense) => (
                <div 
                  key={expense.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedExpenseDetail(expense)}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedExpenseDetail(expense)}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600">
                      💸
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">
                        {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                      </p>
                      {expense.employeeName && (
                        <p className="text-sm text-blue-600 font-medium">👤 {expense.employeeName}</p>
                      )}
                      <p className="text-sm text-gray-500">{expense.description || '-'}</p>
                      <p className="text-xs text-gray-400">{formatDate(expense.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-red-600 text-lg">
                      -{formatCurrency(expense.amount)}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteExpense(expense); }}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
              <div className="bg-white rounded-xl border">
                <Pagination
                  page={expensePaging.page}
                  totalPages={expensePaging.totalPages}
                  total={expensePaging.total}
                  pageSize={expensePaging.pageSize}
                  onPageChange={expensePaging.setPage}
                  onPageSizeChange={expensePaging.setPageSize}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 text-center">
              <p className="text-gray-500">Kharash lama helin</p>
              <Button onClick={openAddExpenseModal} variant="outline" className="mt-4">
                Ku dar Kharash
              </Button>
            </div>
          )}

          {/* Expense Summary by Category */}
          {expenses.length > 0 && (
            <Card title="Kharashka Noociisa">
              <div className="space-y-2">
                {EXPENSE_CATEGORIES.map(cat => {
                  const catTotal = expenses
                    .filter(e => e.category === cat.value)
                    .reduce((sum, e) => sum + (e.amount || 0), 0);
                  if (catTotal === 0) return null;
                  return (
                    <div key={cat.value} className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">{cat.label}</span>
                      <span className="font-medium text-red-600">{formatCurrency(catTotal)}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Add/Edit Account Modal */}
      <Modal
        isOpen={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        title={editingItem ? 'Wax ka bedel Akoonka' : 'Ku dar Akoon'}
        size="sm"
      >
        <form onSubmit={handleAccountSubmit} className="space-y-4">
          <Select
            label="Nooca"
            name="paymentType"
            value={accountForm.paymentType}
            onChange={handleAccountChange}
            options={PAYMENT_TYPES}
            required
          />
          <Input
            label="Magaca Akoonka"
            name="accountName"
            value={accountForm.accountName}
            onChange={handleAccountChange}
            required
          />
          <Input
            label="Lambarka"
            name="accountNumber"
            value={accountForm.accountNumber}
            onChange={handleAccountChange}
            required
          />
          {editingItem && (
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex flex-col gap-3">
              <div className="flex justify-between items-center text-sm font-semibold text-blue-900 mb-2">
                <span>Maamul Lacagta:</span>
              </div>
              <Input
                name="adjustmentAmount"
                type="number"
                min="0"
                step="0.01"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder="Tusaale: 200"
              />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => handleAdjustBalance('add')}
                  disabled={submitting || !adjustmentAmount}
                  className="bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  ➕ Ku dar
                </button>
                <button
                  type="button"
                  onClick={() => handleAdjustBalance('subtract')}
                  disabled={submitting || !adjustmentAmount}
                  className="bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  ➖ Ka jar
                </button>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowAccountModal(false)} fullWidth>
              Ka noqo
            </Button>
            <Button type="submit" loading={submitting} fullWidth>
              Kaydi
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add Expense Modal */}
      <Modal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        title="Ku dar Kharash"
        size="sm"
      >
        <form onSubmit={handleExpenseSubmit} className="space-y-4">
          <Select
            label="Nooca Kharashka"
            name="category"
            value={expenseForm.category}
            onChange={(e) => {
              handleExpenseChange(e);
              // Reset employee selection when category changes
              setExpenseForm(prev => ({ ...prev, category: e.target.value, employeeId: '' }));
            }}
            options={EXPENSE_CATEGORIES}
            required
          />
          {/* Show employee dropdown only for worker-type categories */}
          {matchedDepartment && (
            <Select
              label={`Shaqaalaha (${matchedDepartment})`}
              name="employeeId"
              value={expenseForm.employeeId}
              onChange={handleExpenseChange}
              options={filteredEmployees.map(emp => ({ value: emp.id, label: emp.name }))}
              required
            />
          )}
          <Input
            label="Sharaxaad"
            name="description"
            value={expenseForm.description}
            onChange={handleExpenseChange}
            placeholder="Faahfaahin..."
          />
          <Input
            label="Qadarka ($)"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={expenseForm.amount}
            onChange={handleExpenseChange}
            required
          />
          <Input
            label="Taariikhda"
            name="date"
            type="date"
            value={expenseForm.date}
            onChange={handleExpenseChange}
            required
          />
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowExpenseModal(false)} fullWidth>
              Ka noqo
            </Button>
            <Button type="submit" variant="danger" loading={submitting} fullWidth>
              Ku dar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(selectedTransaction)}
        onClose={() => setSelectedTransaction(null)}
        title="Faahfaahinta transaction"
      >
        {selectedTransaction && (
          <div className="space-y-2 text-sm">
            <p><span className="text-gray-500">Invoice:</span> {selectedTransaction.invoiceNumber}</p>
            <p><span className="text-gray-500">Macmiil:</span> {selectedTransaction.customerName}</p>
            <p><span className="text-gray-500">Lacag:</span> {formatCurrency(selectedTransaction.amount)}</p>
            <p><span className="text-gray-500">Nooca:</span> {selectedTransaction.paymentType}</p>
            <p><span className="text-gray-500">Taariikh:</span> {formatDate(selectedTransaction.transactionDate || selectedTransaction.createdAt, 'dd/MM/yyyy HH:mm')}</p>
            <RecordAuditBlock record={selectedTransaction} />
            {selectedTransaction.invoiceId && selectedTransaction.invoiceId !== 'manual' && (
              <Button className="mt-2" onClick={() => navigate(`/admin/invoices/${selectedTransaction.invoiceId}`)}>
                Arag invoice
              </Button>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(selectedExpenseDetail)}
        onClose={() => setSelectedExpenseDetail(null)}
        title="Faahfaahinta kharash"
      >
        {selectedExpenseDetail && (
          <div className="space-y-2 text-sm">
            <p><span className="text-gray-500">Category:</span> {selectedExpenseDetail.category}</p>
            <p><span className="text-gray-500">Sharaxaad:</span> {selectedExpenseDetail.description || '—'}</p>
            <p><span className="text-gray-500">Qadarka:</span> {formatCurrency(selectedExpenseDetail.amount)}</p>
            <p><span className="text-gray-500">Taariikh:</span> {formatDate(selectedExpenseDetail.date, 'dd/MM/yyyy')}</p>
            {selectedExpenseDetail.employeeName && (
              <p><span className="text-gray-500">Shaqaale:</span> {selectedExpenseDetail.employeeName}</p>
            )}
            <RecordAuditBlock record={selectedExpenseDetail} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PaymentAccounts;